import { create } from "zustand";
import axios from "axios";
import { getToken } from "@/utils/localstorage";
import type { OutlineEntry } from "@/utils/slideOutline";

export type InitialInputsOutput = Record<string, unknown>;

interface PresentationDialogState {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  initialInputs: InitialInputsOutput | null;
  selectedSlides: OutlineEntry[] | null;

  initialize: (
    primaryTicker: string,
    primaryExchange: string,
    primaryCompanyName: string,
    selectedSlides: OutlineEntry[],
  ) => Promise<string>;

  interact: (message: string) => Promise<string>;
  complete: () => Promise<{ initialInputs: InitialInputsOutput; selectedSlides: OutlineEntry[] }>;
  reset: () => Promise<void>;
}

async function authHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function apiBase() {
  const apiUrl = process.env.API_URL;
  if (!apiUrl) {
    throw new Error("API_URL is not set.");
  }
  return apiUrl.replace(/\/$/, "");
}

export const usePresentationDialogStore = create<PresentationDialogState>((set) => ({
  isInitialized: false,
  isLoading: false,
  error: null,
  initialInputs: null,
  selectedSlides: null,

  initialize: async (primaryTicker, primaryExchange, primaryCompanyName, selectedSlides) => {
    set({ isLoading: true, error: null, initialInputs: null, selectedSlides, isInitialized: false });
    try {
      const url = `${apiBase()}/slide-generation/dialog/initialize`;
      const response = await axios.post<{ openingMessage: string }>(
        url,
        {
          source: "ppt",
          flow: "presentation",
          primaryTicker,
          primaryExchange,
          primaryCompanyName,
          selectedSlides,
        },
        { headers: await authHeaders(), withCredentials: true }
      );
      set({ isInitialized: true, isLoading: false });
      return response.data.openingMessage;
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Failed to initialize dialog";
      set({ isLoading: false, error: msg });
      throw new Error(msg);
    }
  },

  interact: async (message) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${apiBase()}/slide-generation/dialog/interact`;
      const response = await axios.post<{ response: string }>(
        url,
        { source: "ppt", flow: "presentation", message },
        { headers: await authHeaders(), withCredentials: true }
      );
      set({ isLoading: false });
      return response.data.response;
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Failed to send message";
      set({ isLoading: false, error: msg });
      throw new Error(msg);
    }
  },

  complete: async () => {
    set({ isLoading: true, error: null });
    try {
      const url = `${apiBase()}/slide-generation/dialog/complete`;
      const response = await axios.post<{
        initialInputs: InitialInputsOutput;
        selectedSlides: OutlineEntry[];
      }>(
        url,
        { source: "ppt", flow: "presentation" },
        { headers: await authHeaders(), withCredentials: true }
      );
      const { initialInputs, selectedSlides } = response.data;
      set({ isLoading: false, initialInputs, selectedSlides, isInitialized: false });
      return { initialInputs, selectedSlides };
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Failed to complete dialog";
      set({ isLoading: false, error: msg });
      throw new Error(msg);
    }
  },

  reset: async () => {
    try {
      const url = `${apiBase()}/slide-generation/dialog/reset`;
      await axios.post(
        url,
        { source: "ppt", flow: "presentation" },
        { headers: await authHeaders(), withCredentials: true }
      );
    } catch {
      // best-effort
    }
    set({ isInitialized: false, isLoading: false, error: null, initialInputs: null, selectedSlides: null });
  },
}));

